/**
 * Services Page - Similar to Products but for services
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Plus, Briefcase, Edit, Trash2 } from 'lucide-react';

import ServiceModal from './ServiceModal';
import './styles.css';

export default function Services() {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();
  const gridRef = useRef(null);

  // Data state
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Server-side pagination state
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({});

  // Load services with server-side params
  const loadServices = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build filters object - always filter by type=service
      const apiFilters = { type: 'service' };
      if (filters.name) apiFilters.name = filters.name;
      if (filters.category_id && filters.category_id !== 'all') apiFilters.category_id = filters.category_id;
      if (filters.status && filters.status !== 'all') apiFilters.is_active = filters.status === 'active';

      const res = await api.post('/products/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: apiFilters,
      });

      if (res.data.success) {
        setServices(res.data.data.items);
        setTotalItems(res.data.data.totalItems);
      }
    } catch (err) {
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters]);

  // Load categories - only service categories
  const loadCategories = async () => {
    try {
      const res = await api.get('/categories?type=service');
      setCategories(res.data);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  useEffect(() => {
    loadServices();
    loadCategories();
  }, [loadServices]);

  // Handlers
  const handleNew = () => {
    setEditingService(null);
    setModalOpen(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setModalOpen(true);
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      // Force type to service
      const serviceData = { ...data, type: 'service', is_service: true, track_stock: false };

      if (editingService) {
        await api.put(`/products/${editingService.id}`, serviceData);
        toast.success('Serviço atualizado com sucesso!');
      } else {
        await api.post('/products', serviceData);
        toast.success('Serviço criado com sucesso!');
      }

      setModalOpen(false);
      setEditingService(null);
      loadServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar serviço');
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingService to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingService(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/products/${deleteId}`);
      toast.success('Serviço excluído com sucesso!');
      loadServices();
    } catch (err) {
      toast.error('Erro ao excluir serviço');
    } finally {
      setDeleteId(null);
    }
  };

  // Column definitions
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 2,
      minWidth: 200,
      cellRenderer: (params) => (
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{params.value}</span>
        </div>
      ),
    },
    {
      field: 'category_name',
      headerName: 'Categoria',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'price',
      headerName: 'Preço',
      width: 120,
      cellRenderer: (params) => (
        <span className="font-mono font-semibold text-primary">
          {formatCurrency(params.value)}
        </span>
      ),
    },
    {
      field: 'description',
      headerName: 'Descrição',
      flex: 2,
      minWidth: 200,
      cellRenderer: (params) => (
        <span className="text-muted-foreground truncate">
          {params.value || '-'}
        </span>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 100,
      cellRenderer: (params) => (
        <Badge variant={params.value ? 'default' : 'secondary'}>
          {params.value ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 100,
      sortable: false,
      cellRenderer: (params) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={() => handleEdit(params.data)}>
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleteId(params.data.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ], [formatCurrency]);

  // Filter definitions
  const filterDefinitions = [
    {
      key: 'name',
      label: 'Nome',
      type: 'text',
      placeholder: 'Buscar por nome...',
    },
    {
      key: 'category_id',
      label: 'Categoria',
      type: 'select',
      placeholder: 'Todas as categorias',
      options: [
        { value: 'all', label: 'Todas as categorias' },
        ...categories.map(c => ({ value: c.id, label: c.name })),
      ],
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'active', label: 'Ativos' },
        { value: 'inactive', label: 'Inativos' },
      ],
    },
  ];

  return (
    <div className="services-page" data-testid="services-page">
      <PageHeader
        icon={Briefcase}
        title="Serviços"
        subtitle={`${totalItems} serviços cadastrados`}
        actions={
          <Button onClick={handleNew} data-testid="new-service-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Serviço
          </Button>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setPageNumber(1);
        }}
        onSearch={() => loadServices()}
        onClearFilters={() => {
          setFilters({});
          setPageNumber(1);
        }}
      />

      <DataGrid
        ref={gridRef}
        title="Lista de Serviços"
        rowData={services}
        columnDefs={columns}
        loading={loading}
        pagination={true}
        paginationPageSize={pageSize}
        serverSidePagination={true}
        totalItems={totalItems}
        currentPage={pageNumber}
        onPageChange={(newPage) => setPageNumber(newPage)}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPageNumber(1);
        }}
        onSortChanged={(params) => {
          const sortModel = params.api?.getSortModel?.() || [];
          if (sortModel.length > 0) {
            setSortBy(sortModel[0].colId);
            setSortDirection(sortModel[0].sort);
          }
        }}
      />

      {/* Service Modal */}
      <ServiceModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        service={editingService}
        categories={categories}
        onSave={handleSave}
        loading={saving}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este serviço? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
